import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const About: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: false });
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setCounter(0);
      return;
    }
    
    // Animated counter for clients
    const targetCount = 5000;
    const duration = 2000; // 2 seconds
    const increment = targetCount / (duration / 16);
    
    let currentCount = 0;
    const timer = setInterval(() => {
      currentCount += increment;
      if (currentCount >= targetCount) {
        setCounter(targetCount);
        clearInterval(timer);
      } else {
        setCounter(Math.floor(currentCount));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isVisible]);

  const features = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Profissionais Qualificados',
      description: 'Equipe especializada e certificada'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: 'Tecnologia Avançada',
      description: 'Equipamentos de última geração'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'Atendimento Personalizado',
      description: 'Foco nas necessidades individuais'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      title: 'Resultados Naturais',
      description: 'Beleza harmoniosa e equilibrada'
    }
  ];

  return (
    <section ref={elementRef as React.RefObject<HTMLElement>} className="py-20 md:py-24 bg-gradient-to-br from-white via-[#FCFCFD] to-gray-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/5 to-transparent pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
      
      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left side - Image with Badge */}
          <div className={`relative order-2 lg:order-1 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="relative group">
              {/* Main image container */}
              <div className="aspect-[3/4] rounded-3xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-2xl transform transition-all duration-700 group-hover:scale-[1.02] group-hover:shadow-3xl">
                <img 
                  src="/assets/img/tambia.jpg" 
                  alt="Clínica BotoHarmony" 
                  className="w-full h-full object-cover object-center"
                  style={{ objectPosition: '25% center' }}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              
              {/* Floating badge - Clients count */}
              <div className="absolute -bottom-8 -right-8 animate-float">
                <div className="relative group/badge">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary to-primary rounded-2xl blur-xl opacity-50 group-hover/badge:opacity-75 transition-opacity duration-300"></div>
                  
                  {/* Badge content */}
                  <div className="relative bg-gradient-to-br from-secondary to-primary text-white p-8 rounded-2xl shadow-2xl transform transition-all duration-300 hover:scale-110 cursor-pointer">
                    <div className="text-center">
                      <span className="block text-5xl font-bold mb-1">{counter}+</span>
                      <span className="text-sm font-semibold uppercase tracking-wider">Clientes</span>
                      <span className="block text-sm font-semibold uppercase tracking-wider">Satisfeitos</span>
                    </div>
                    
                    {/* Decorative corner accent */}
                    <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-white/50 rounded-tr-lg"></div>
                    <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-white/50 rounded-bl-lg"></div>
                  </div>
                </div>
              </div>
              
              {/* Decorative accent line */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-4 border-secondary/20 rounded-3xl -z-10 group-hover:scale-110 transition-transform duration-500"></div>
            </div>
          </div>
          
          {/* Right side - Content */}
          <div className={`order-1 lg:order-2 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="max-w-xl">
              {/* Title */}
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-6 leading-tight">
                Sobre a <span className="text-secondary">BotoHarmony</span>
              </h2>
              
              {/* Description paragraphs */}
              <div className="space-y-4 mb-8">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Fundada com a missão de proporcionar tratamentos estéticos de excelência, a BotoHarmony se destaca pela abordagem personalizada e resultados naturais que respeitam a individualidade de cada cliente.
                </p>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                  Nossa equipe é formada por profissionais altamente qualificados e apaixonados por estética, sempre atualizados com as técnicas mais avançadas e seguras do mercado.
                </p>
              </div>
              
              {/* Features grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className={`group/item flex items-start gap-3 p-4 rounded-xl transition-all duration-300 cursor-pointer ${
                      hoveredFeature === index 
                        ? 'bg-gradient-to-br from-secondary/10 to-primary/5 shadow-md transform scale-105' 
                        : 'bg-white/50 hover:bg-white shadow-sm'
                    }`}
                    onMouseEnter={() => setHoveredFeature(index)}
                    onMouseLeave={() => setHoveredFeature(null)}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      hoveredFeature === index 
                        ? 'bg-gradient-to-br from-secondary to-primary text-white scale-110 rotate-3' 
                        : 'bg-gray-100 text-secondary'
                    }`}>
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-bold text-sm mb-1 transition-colors duration-300 ${
                        hoveredFeature === index ? 'text-secondary' : 'text-gray-800'
                      }`}>
                        {feature.title}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* CTA Button */}
              <Link 
                to="/quem-somos" 
                className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-secondary to-primary text-white font-semibold text-base rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 relative overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-primary to-secondary transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
                <span className="relative z-10">Conheça Nossa História</span>
                <svg 
                  className="relative z-10 w-5 h-5 transform transition-transform duration-300 group-hover:translate-x-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default About;